'use client'

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Command, CommandInput, CommandList, CommandItem, CommandGroup, CommandEmpty } from '@/components/ui/command'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { Check, ChevronsUpDown } from 'lucide-react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { cn } from '@/lib/utils/utils'
import { ReactNode, useState } from 'react'
import { SelectItemProps } from '@/types/types'
import { formFieldComboboxTriggerClassName, formFieldSelectTriggerClassName } from '@/components/form/formFieldSurface'
import { CountryFlagIcon } from '@/components/form/CountryFlagIcon'

const popoverCommandItemClassName =
    'cursor-pointer rounded-xl px-3 py-2.5 text-slate-600 data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 transition-colors'

function renderDefaultSelectItem(item: SelectItemProps) {
    return (
        <span className='flex min-w-0 items-center gap-3'>
            {(item.icon || item.emoji) && <CountryFlagIcon src={item.icon} emoji={item.emoji} className='h-4 w-6' />}
            <span className='truncate font-medium'>{item.description || 'No description'}</span>
        </span>
    )
}

/**
 * Select form field component with popover or dropdown support
 * @template TFormValues - The form values type extending FieldValues
 */
interface SelectFormFieldProps<TFormValues extends FieldValues> {
    form: UseFormReturn<TFormValues>
    name: Path<TFormValues>
    label?: string
    /** Label above field, trigger always full width (no side-by-side layout on large screens) */
    stackLabel?: boolean
    items: SelectItemProps[]
    type: 'popover' | 'select'
    disabled?: boolean
    placeholder?: string
    searchPlaceholder?: string
    itemRenderer?: (item: SelectItemProps) => ReactNode
    onValueChange?: (value: string) => void
    defaultValue?: string
}

export const SelectFormField = <TFormValues extends FieldValues>({
    form,
    name,
    label,
    stackLabel,
    items,
    type,
    disabled,
    placeholder = '',
    searchPlaceholder = 'Search...',
    itemRenderer,
    onValueChange,
    defaultValue
}: SelectFormFieldProps<TFormValues>) => {
    const stacked = stackLabel ?? false
    const [search, setSearch] = useState<string>('')
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const selectedItem = items.find((item) => item.id === field.value)

                return (
                    <FormItem>
                        <div
                            className={cn(
                                'flex flex-col',
                                label && !stacked && 'lg:flex-row',
                                stacked && label && 'gap-2'
                            )}>
                            {label && (
                                <FormLabel
                                    className={cn(stacked ? 'text-left' : 'lg:basis-1/2 content-center mb-2 lg:mb-0')}>
                                    {label}
                                </FormLabel>
                            )}
                            <FormControl className={cn(label && !stacked && 'lg:basis-1/2', 'w-full min-w-0')}>
                                {type === 'popover' ? (
                                    <Popover
                                        open={isPopoverOpen}
                                        onOpenChange={(open) => {
                                            setIsPopoverOpen(open)
                                            if (!open) setSearch('')
                                        }}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                type='button'
                                                role='combobox'
                                                aria-expanded={isPopoverOpen}
                                                disabled={disabled}
                                                className={cn(
                                                    formFieldComboboxTriggerClassName,
                                                    'hover:bg-card hover:text-foreground',
                                                    label && !stacked && 'lg:basis-1/2',
                                                    !field.value && 'text-muted-foreground'
                                                )}>
                                                <span className='flex min-w-0 flex-1 items-center truncate text-left'>
                                                    {selectedItem
                                                        ? itemRenderer
                                                            ? itemRenderer(selectedItem)
                                                            : renderDefaultSelectItem(selectedItem)
                                                        : placeholder}
                                                </span>
                                                <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            align='start'
                                            side='bottom'
                                            sideOffset={6}
                                            collisionPadding={12}
                                            className='z-50 w-(--radix-popover-trigger-width) min-w-[18rem] overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-0 shadow-[0_18px_50px_-12px_rgba(15,15,15,0.14),0_8px_24px_-8px_rgba(15,15,15,0.08)]'
                                            onWheel={(event) => event.stopPropagation()}
                                            onTouchMove={(event) => event.stopPropagation()}>
                                            <Command shouldFilter={false} className='form-combobox-search bg-white'>
                                                <CommandInput
                                                    value={search}
                                                    onValueChange={setSearch}
                                                    placeholder={searchPlaceholder}
                                                    wrapperClassName='border-b border-slate-100/50 px-4'
                                                    className='h-11 rounded-none border-0 text-sm shadow-none placeholder:text-slate-400'
                                                />
                                                <CommandList className='max-h-[min(60vh,24rem)] overflow-y-auto overscroll-contain p-2'>
                                                    <CommandEmpty className='py-10 text-sm text-slate-500'>
                                                        No results found.
                                                    </CommandEmpty>
                                                    <CommandGroup className='p-0'>
                                                        {items
                                                            .filter((item) =>
                                                                (item.description || '')
                                                                    .toLowerCase()
                                                                    .includes(search.toLowerCase())
                                                            )
                                                            .map((item) => (
                                                                <CommandItem
                                                                    value={item.id}
                                                                    key={item.id}
                                                                    onSelect={() => {
                                                                        field.onChange(item.id)
                                                                        onValueChange?.(item.id)
                                                                        setIsPopoverOpen(false)
                                                                        setSearch('')
                                                                    }}
                                                                    className={cn(
                                                                        popoverCommandItemClassName,
                                                                        'flex items-center justify-between'
                                                                    )}>
                                                                    <div className='flex min-w-0 items-center gap-3'>
                                                                        {itemRenderer
                                                                            ? itemRenderer(item)
                                                                            : renderDefaultSelectItem(item)}
                                                                    </div>
                                                                    <Check
                                                                        className={cn(
                                                                            'ml-2 h-4 w-4 shrink-0 text-petrol',
                                                                            item.id === field.value
                                                                                ? 'opacity-100'
                                                                                : 'opacity-0'
                                                                        )}
                                                                    />
                                                                </CommandItem>
                                                            ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                ) : (
                                    <Select
                                        disabled={disabled ? disabled : false}
                                        onValueChange={(value) => {
                                            field.onChange(value)
                                            onValueChange?.(value)
                                        }}
                                        value={field.value}
                                        defaultValue={defaultValue ?? ''}>
                                        <SelectTrigger
                                            className={cn(
                                                formFieldSelectTriggerClassName,
                                                label && !stacked && 'lg:basis-1/2'
                                            )}>
                                            <SelectValue placeholder={placeholder} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {items.map((item) => (
                                                <SelectItem key={item.id} value={item.id} className='cursor-pointer'>
                                                    {item.description || 'No description'}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </FormControl>
                        </div>
                        <FormMessage className={label && !stacked ? 'text-right' : ''} />
                    </FormItem>
                )
            }}
        />
    )
}
